// 03 — what came back: raw and cleaned text side by side with the cleanup
// judge's reading (or "not judged" while no judge is configured), the flag,
// provenance and the response id; or the error with its child-facing outcome
// and its full kind.
import { Badge } from "@bindery/ui/components/ui/badge";
import { cn } from "@bindery/ui/lib/utils";
import type { SubmissionState } from "@bindery/speech/client";
import type { Transcript } from "@bindery/speech/contract";
import type { ReactNode } from "react";
import { useNow } from "../lib/hooks";
import { ErrorReadout } from "./error-readout";
import { JudgementReadout } from "./judgement-readout";
import { Absent, Panel, Readout, Readouts } from "./panel";

// A client-side stopwatch around the POST: the provider round trip plus
// the upload, which is what a person at a device actually waits through.
export interface RoundTrip {
  readonly startedAt: number;
  readonly endedAt: number | null;
}

function TextColumn({
  label,
  text,
  aside,
}: {
  label: string;
  text: string | null;
  aside?: ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <div className="flex min-h-5 flex-wrap items-center gap-2">
        <span className="lab-label">{label}</span>
        {aside}
      </div>
      <p
        className={cn(
          "bg-background/60 min-h-24 rounded-sm border px-3 py-2 text-[0.95rem] leading-relaxed whitespace-pre-wrap",
          text === null && "text-muted-foreground italic",
        )}
      >
        {text ?? "No cleanup ran — disabled, or the cleanup call failed."}
      </p>
    </div>
  );
}

// The cleaned column's one-line summary; the questions themselves are
// JudgementReadout's, under both texts.
function JudgeTag({ cleanup }: { cleanup: Transcript["cleanup"] }) {
  if (cleanup === null) return null;
  const { judgement } = cleanup;
  return (
    <Badge variant="outline" className="lab-readout rounded-sm">
      {judgement === null
        ? "not judged"
        : `meaning ${judgement.preservesMeaning.toFixed(2)}`}
    </Badge>
  );
}

function Elapsed({ roundTrip }: { roundTrip: RoundTrip }) {
  const now = useNow(roundTrip.endedAt === null);
  const ms = (roundTrip.endedAt ?? now) - roundTrip.startedAt;
  return <>{Math.max(0, Math.round(ms)).toLocaleString("en-US")} ms</>;
}

export function ResultPanel({
  submission,
  roundTrip,
}: {
  submission: SubmissionState;
  roundTrip: RoundTrip | null;
}) {
  return (
    <Panel
      index="03"
      title="Result"
      aside={
        roundTrip === null ? null : (
          <span className="lab-readout text-muted-foreground text-xs">
            round trip <Elapsed roundTrip={roundTrip} />
          </span>
        )
      }
    >
      {submission.tag === "idle" ? (
        <p className="text-muted-foreground text-sm">
          <Absent>Nothing submitted.</Absent> Record, listen back, then
          Transcribe. Nothing is stored — not the audio, not the transcript.
        </p>
      ) : submission.tag === "submitting" ? (
        <p className="lab-readout text-sm">
          Uploading and transcribing
          <span className="lab-pulse">…</span>
        </p>
      ) : submission.tag === "failed" ? (
        <ErrorReadout error={submission.error} />
      ) : (
        <Transcribed submission={submission} />
      )}
    </Panel>
  );
}

function Transcribed({
  submission,
}: {
  submission: Extract<SubmissionState, { tag: "transcribed" }>;
}) {
  const { transcript, provenance, responseId } = submission;
  const { quality } = transcript;
  return (
    <>
      <div className="grid gap-3 md:grid-cols-2">
        <TextColumn label="Raw (ASR)" text={transcript.raw} />
        <TextColumn
          label="Cleaned"
          text={transcript.cleanup?.text ?? null}
          aside={<JudgeTag cleanup={transcript.cleanup} />}
        />
      </div>
      {transcript.cleanup === null ? null : (
        <JudgementReadout judgement={transcript.cleanup.judgement} />
      )}
      <div className="grid gap-x-6 gap-y-3 border-t pt-3 md:grid-cols-2">
        <Readouts>
          <Readout label="flagged">
            {transcript.flagged ? (
              <span className="text-destructive font-medium">
                yes — a flagged word matched
              </span>
            ) : (
              "no"
            )}
          </Readout>
          <Readout label="provider">{provenance.provider}</Readout>
          <Readout label="asr model">{provenance.models.asr}</Readout>
          <Readout label="cleanup model">
            {provenance.models.cleanup ?? <Absent>none</Absent>}
          </Readout>
        </Readouts>
        <Readouts>
          <Readout label="response id">{responseId}</Readout>
          <Readout label="received">
            {new Date(provenance.receivedAt).toISOString()}
          </Readout>
          <Readout label="language">
            {transcript.language ?? <Absent>not reported</Absent>}
          </Readout>
          <Readout label="quality">
            {quality.meanWordConfidence === undefined &&
            quality.noSpeechProbability === undefined ? (
              <Absent>not reported</Absent>
            ) : (
              [
                quality.meanWordConfidence === undefined
                  ? null
                  : `word conf ${quality.meanWordConfidence.toFixed(3)}`,
                quality.noSpeechProbability === undefined
                  ? null
                  : `no-speech p ${quality.noSpeechProbability.toFixed(3)}`,
              ]
                .filter((part) => part !== null)
                .join(" · ")
            )}
          </Readout>
        </Readouts>
      </div>
      <details className="group border-t pt-2">
        <summary className="lab-label cursor-pointer select-none">
          Response JSON
        </summary>
        <pre className="lab-readout bg-background/60 mt-2 max-h-80 overflow-auto rounded-sm border p-2 text-xs">
          {JSON.stringify({ responseId, transcript, provenance }, null, 2)}
        </pre>
      </details>
    </>
  );
}
